import { useParams, Outlet, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { HouseholdLayout } from '@/components/layouts/HouseholdLayout.jsx';
import { Box, Sheet, Typography } from '@mui/joy';
import { Button } from '@/ui/Button';
import { WeeklyCalendar } from '../components/WeeklyCalendar';
import { Stack, Tooltip } from '@mui/joy';
import { useGetMealPlanEntriesQuery } from '../api';
import { useGetHouseholdQuery } from '@/features/households/api';
import { useTheme, useMediaQuery } from '@mui/material';
import { AddNavigationButton } from '@/components/AddNavigationButton';
import { LAST_MEALS_PAGE_KEY } from '@/features/meals/constants';
import { useDateSelection } from '@/features/meals/hooks/useDateSelection';
import { PAGE_MAX_WIDTH } from '@/theme.js';

export function MealPlanPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { householdId } = useParams();
  const { data: householdData } = useGetHouseholdQuery({ householdId });  
  
  const weekStartDay = householdData?.data?.week_start_day ?? 1;
  
  const { selectedDateString, currentWeek, endOfWeek, onDateChange } = useDateSelection({ 
    weekStartDay, 
    isReady: !!householdData 
  });
  
  const { data: mealPlan } = useGetMealPlanEntriesQuery({ householdId });

  const weeklyMealPlanEntries = useMemo(() => {
    return mealPlan?.data?.filter(entry => {
      const entryDate = new Date(entry.date);    
      return entryDate >= currentWeek.getTime() && entryDate <= endOfWeek.getTime();
    }) || [];    
  }, [mealPlan, currentWeek, endOfWeek]);

  const hasMeals = weeklyMealPlanEntries.length > 0;

  useEffect(() => {
    localStorage.setItem(LAST_MEALS_PAGE_KEY, 'plan');
  }, []);

  return (
    <HouseholdLayout backgroundColor="gray" flushHeader addAction={isMobile && <AddNavigationButton disabled={!hasMeals} to={`/h/${householdId}/meals/plan/add-to-shopping-list?date=${selectedDateString}`} />}>
      <Stack gap={2} direction="column" sx={{ pb: isMobile ? 0 : 10, minWidth: 0 }}>
        <WeeklyCalendar
          householdId={householdId}
          currentWeek={currentWeek}
          selectedDate={selectedDateString}
          onDateChange={onDateChange}
          weekStartDay={weekStartDay}
        />
      </Stack>
      {!isMobile && (
        <Sheet
          variant="plain"
          sx={{
            position: 'fixed',
            bottom: isTablet ? '62px' : 0,
            left: 0,
            right: 0,
            zIndex: 900,
            px: { xs: 2, md: 3 },
            backgroundColor: 'var(--joy-palette-neutral-100)',
          }}
        >
          <Stack direction="row" gap={1} alignItems="center" sx={{ maxWidth: PAGE_MAX_WIDTH, margin: '0 auto', py: { xs: 2, md: 2.5 } }}>
            <Typography level="title-xs" sx={{ flex: 1 }}>{weeklyMealPlanEntries.length} meal{weeklyMealPlanEntries.length > 1 ? 's' : ''} planned</Typography>
            <Tooltip title={hasMeals ? null : 'No meals planned for the current week'} placement="bottom-end">
              <Box>
                <Button
                  type="wider"
                  component={Link}
                  to={`/h/${householdId}/meals/plan/add-to-shopping-list?date=${selectedDateString}`}
                  disabled={!hasMeals}
                  sx={{ opacity: hasMeals ? 1 : 0.5, width: { xs: '100%', md: 'auto' } }}
                >Build shopping list</Button>
              </Box>
            </Tooltip>
          </Stack>
        </Sheet>
      )}
      <Outlet />
    </HouseholdLayout>
  );
}

